import React, { useState } from "react";
import { Menu, MenuItem, Box, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import CustomButton from "./CustomButton";

const ANDROID_APPS = [
  { key: "accountExecutive", link: "https://play.google.com/store/apps/details?id=com.biometricproaccountexecutive1" },
  { key: "companyAdmin", link: "https://play.google.com/store/apps/details?id=com.biometricprocompanyadminapp" },
  { key: "employee", link: "https://play.google.com/store/apps/details?id=com.biometricproemployeeapp" },
];

const IOS_APPS = [
  { key: "accountExecutive", link: "https://apps.apple.com/us/app/account-executive-biometricpro/id6752927067" },
  { key: "companyAdmin", link: "https://apps.apple.com/us/app/company-admin-biometric-pro/id6788851657" },
  { key: "employee", link: "https://apps.apple.com/us/app/employee-biometric-pro/id6790351839" },
];

const ROLE_LABEL_KEYS = {
  accountExecutive: "loginRoles.accountExecutive",
  companyAdmin: "loginRoles.companyAdmin",
  employee: "loginRoles.worker",
};

/**
 * Drop-in replacement for CustomButton that opens the same Android/iOS
 * app-download menu used in the site header, instead of a direct link.
 */
const GetAppButton = ({ label, ...buttonProps }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <CustomButton
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {label}
            <ExpandMoreIcon sx={{ fontSize: 18 }} />
          </Box>
        }
        onClick={handleOpen}
        {...buttonProps}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            border: "1px solid #e0e0e0",
            padding: "8px 4px",
            maxWidth: "calc(100vw - 24px)",
          },
        }}
      >
        <Box sx={{ display: "flex", gap: { xs: 1, sm: 2 }, px: 1 }}>
          <Box sx={{ minWidth: { xs: 110, sm: 150 }, flex: "1 1 0" }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                color: "#8a8a8a",
                fontFamily: "'Poppins', sans-serif",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                padding: { xs: "4px 8px", sm: "4px 12px" },
              }}
            >
              {t("Android")}
            </Typography>
            {ANDROID_APPS.map((app) => (
              <MenuItem
                key={`android-${app.key}`}
                onClick={() => {
                  handleClose();
                  window.open(app.link, "_blank");
                }}
                sx={{
                  py: 0.6,
                  px: { xs: 1, sm: 2 },
                  whiteSpace: "normal",
                  "&:hover": { backgroundColor: "rgba(0,110,194,0.08)" },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontFamily: "'Poppins', sans-serif",
                    color: "#333",
                    lineHeight: 1.3,
                    overflowWrap: "break-word",
                  }}
                >
                  {t(ROLE_LABEL_KEYS[app.key])}
                </Typography>
              </MenuItem>
            ))}
          </Box>
          <Box sx={{ minWidth: { xs: 110, sm: 150 }, flex: "1 1 0", borderLeft: "1px solid #eee" }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                color: "#8a8a8a",
                fontFamily: "'Poppins', sans-serif",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                padding: { xs: "4px 8px", sm: "4px 12px" },
              }}
            >
              {t("iOS")}
            </Typography>
            {IOS_APPS.map((app) => (
              <MenuItem
                key={`ios-${app.key}`}
                onClick={() => {
                  handleClose();
                  window.open(app.link, "_blank");
                }}
                sx={{
                  py: 0.6,
                  px: { xs: 1, sm: 2 },
                  whiteSpace: "normal",
                  "&:hover": { backgroundColor: "rgba(0,110,194,0.08)" },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontFamily: "'Poppins', sans-serif",
                    color: "#333",
                    lineHeight: 1.3,
                    overflowWrap: "break-word",
                  }}
                >
                  {t(ROLE_LABEL_KEYS[app.key])}
                </Typography>
              </MenuItem>
            ))}
          </Box>
        </Box>
      </Menu>
    </>
  );
};

export default GetAppButton;
